import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Clock, Award } from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import aboutUsImage from '../../images/AboutUs/AboutUs.jpg';
import aboutHeroImage from '../../images/herosection/heromain.jpg';
import './About.css';

const About = () => {
  const values = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Quality & Reliability',
      description: 'We maintain the highest standards in all our services and spare parts.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Customer Focus',
      description: 'Your success is our priority. We build lasting partnerships.'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: '24/7 Support',
      description: 'Round-the-clock technical assistance for critical operations.'
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Expertise',
      description: '25+ years of experience with certified technicians and engineers.'
    }
  ];

  const milestones = [
    { year: '2005', title: 'KirBOO Flowsafe Founded', description: 'Started as KirBOO Flowsafe with a vision for reliable industrial flow control and field service' },
    { year: '2018', title: 'Service Expansion', description: 'Expanded services to cover all major valve and actuator brands' },
    { year: '2020', title: 'Pan India Presence', description: 'Established a service network across India for on-site and workshop support' },
    { year: '2022', title: 'KirBOO Flowsafe Pvt Ltd', description: 'Transformed into KirBOO Flowsafe Pvt Ltd, strengthening governance and scale for customers nationwide' },
    { year: '2024', title: 'Industry Leader', description: 'Recognized as a trusted partner in high-pressure valves, actuators, pumps, and spares' }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section 
        className="about-hero"
        style={{
          backgroundImage: `url(${aboutHeroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="about-hero-overlay"></div>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="about-hero-content"
          >
            <Breadcrumb />
            <h1 className="about-hero-title">
              About <span className="about-hero-title-highlight">KirBOO Flowsafe</span>
            </h1>
            <p className="about-hero-description">
              Your trusted partner in High-pressure valves, Electric & Pneumatic actuators, 
              Pump Services and spare parts support with 24/7 technical assistance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="about-story">
        <div className="container-custom">
          <div className="about-story-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="about-story-content"
            >
              <h2 className="about-story-title">Our Story</h2>
              <p className="about-story-description">
                Founded in 2005 as <strong>KirBOO Flowsafe</strong>, we built our reputation on dependable field 
                and workshop service for high-pressure valves, electric and pneumatic actuators, pumps, and spares. 
                In <strong>2022</strong> we transformed into <strong>KirBOO Flowsafe Pvt Ltd</strong>, bringing 
                the same hands-on expertise under a formal corporate structure to serve customers across India.
              </p>
              <p className="about-story-description">
                Today, with <strong>25+ years of experience</strong> and a trusted name since 2005, we pride ourselves 
                on expertise in flow control — not just as a service provider but as a partner dedicated to the seamless 
                operation of your critical systems and processes.
              </p>
              <p className="about-story-description">
                Our commitment to quality, reliability, and customer satisfaction has made us a leading force 
                in the industry. We understand the critical nature of your operations and provide comprehensive 
                solutions for your flow control needs.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="about-story-visual"
            >
              <div className="about-serving-gallery about-serving-gallery--single">
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="about-serving-main-image"
                >
                  <img src={aboutUsImage} alt="About KirBOO Flowsafe" className="about-serving-img" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="about-values">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="about-values-header"
          >
            <h2 className="about-values-title">Our Core Values</h2>
            <p className="about-values-description">
              The principles that guide everything we do
            </p>
          </motion.div>
          
          <div className="about-values-grid">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="about-value-card"
              >
                <div className="about-value-icon">
                  <div className="about-value-icon-wrapper">
                    {value.icon}
                  </div>
                </div>
                <h3 className="about-value-title">{value.title}</h3>
                <p className="about-value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="about-milestones">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="about-milestones-header"
          >
            <h2 className="about-milestones-title">Our Journey</h2>
            <p className="about-milestones-description">
              Key milestones in our growth and success
            </p>
          </motion.div>
          
          <div className="about-milestones-timeline">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`about-milestone-item ${index % 2 === 0 ? 'about-milestone-left' : 'about-milestone-right'}`}
              >
                <div className="about-milestone-content">
                  <div className="about-milestone-year">{milestone.year}</div>
                  <h3 className="about-milestone-title">{milestone.title}</h3>
                  <p className="about-milestone-description">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="about-company-info">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="about-company-info-content"
          >
            <h2 className="about-company-info-title">Company Information</h2>
            <div className="about-company-info-grid">
              <div className="about-company-info-item">
                <h3 className="about-company-info-label">GST Number</h3>
                <p className="about-company-info-value">27AAJCK4096N1ZS</p>
              </div>
              <div className="about-company-info-item">
                <h3 className="about-company-info-label">CIN</h3>
                <p className="about-company-info-value">U29256MH2022PTC376818</p>
              </div>
              <div className="about-company-info-item">
                <h3 className="about-company-info-label">ISO Certification</h3>
                <p className="about-company-info-value">9001:2015</p>
                <p className="about-company-info-cert">1011-QMS-BA-09-2202-UNAB-001685-IN</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="about-team">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="about-team-header"
          >
            <h2 className="about-team-title">Our Expert Team</h2>
            <p className="about-team-description">
              Meet the professionals behind our success
            </p>
          </motion.div>
          
          <div className="about-team-grid">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="about-team-card"
            >
              <div className="about-team-image">
                <div className="about-team-image-placeholder">
                  <div className="about-team-gradient-1">
                    <Users className="w-12 h-12 text-white" />
                  </div>
                </div>
              </div>
              <h3 className="about-team-name">Certified Technicians</h3>
              <p className="about-team-role">Factory-trained experts with 25+ years of combined industry experience</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="about-team-card"
            >
              <div className="about-team-image">
                <div className="about-team-image-placeholder">
                  <div className="about-team-gradient-2">
                    <Award className="w-12 h-12 text-white" />
                  </div>
                </div>
              </div>
              <h3 className="about-team-name">Engineering Team</h3>
              <p className="about-team-role">Specialized engineers for complex technical solutions</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="about-team-card"
            >
              <div className="about-team-image">
                <div className="about-team-image-placeholder">
                  <div className="about-team-gradient-3">
                    <Clock className="w-12 h-12 text-white" />
                  </div>
                </div>
              </div>
              <h3 className="about-team-name">Support Staff</h3>
              <p className="about-team-role">24/7 customer support and technical assistance</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
